import { useState, useEffect, useCallback, useRef } from "react";
import { cacheGet, cacheGetStale, cacheSet } from "@/lib/cache";

const PAGE_SIZE = 12;

/**
 * Manages an infinite-scroll feed with:
 *   - Page-based accumulation (items append, never replace)
 *   - localStorage TTL caching per page
 *   - Stale-data fallback when the network is down
 *   - Duplicate filtering by item.id across pages
 *   - Error handling with retry for both initial load and "load more"
 *
 * @param {Object}   opts
 * @param {Function} opts.fetchFn      - (page: number) => Promise<Item[]>
 * @param {string}   opts.feedKey      - Unique key; changing it resets the feed
 * @param {Function} [opts.getCacheKey]- (page: number) => string  (omit = no cache)
 * @param {number}   [opts.ttlMs]      - Cache TTL in milliseconds (0 = no cache)
 *
 * @returns {{ items, loading, loadingMore, hasMore, error, stale, loadMore, retryInitial, retryMore, page }}
 */
export function useInfiniteFeed({ fetchFn, feedKey, getCacheKey = null, ttlMs = 0 }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  // Retry counter — incrementing it re-triggers the initial-load effect
  const [initialRetry, setInitialRetry] = useState(0);

  // Refs so callbacks never go stale without re-creating observers
  const fetchFnRef = useRef(fetchFn);
  const getCacheKeyRef = useRef(getCacheKey);
  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);

  useEffect(() => { fetchFnRef.current = fetchFn; }, [fetchFn]);
  useEffect(() => { getCacheKeyRef.current = getCacheKey; }, [getCacheKey]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  // ── Initial load / feed reset ──────────────────────────────────────────────
  useEffect(() => {
    isFetchingRef.current = false;
    setItems([]);
    setPage(1);
    setHasMore(true);
    hasMoreRef.current = true;
    setError(null);
    setStale(false);
    setLoading(true);
    setLoadingMore(false);

    let alive = true;
    const key = getCacheKeyRef.current?.(1);
    const cached = key && ttlMs > 0 ? cacheGet(key, ttlMs) : null;

    if (cached) {
      const hm = cached.hasMore ?? (cached.items?.length >= PAGE_SIZE);
      setItems(cached.items ?? []);
      setHasMore(hm);
      hasMoreRef.current = hm;
      setLoading(false);
      return;
    }

    isFetchingRef.current = true;

    fetchFnRef.current(1)
      .then((result) => {
        if (!alive) return;
        const newItems = Array.isArray(result) ? result : (result?.items ?? []);
        const hm = result?.has_more ?? result?.hasMore ?? (newItems.length >= PAGE_SIZE);
        setItems(newItems);
        setHasMore(hm);
        hasMoreRef.current = hm;
        setStale(false);
        if (key) cacheSet(key, { items: newItems, hasMore: hm });
      })
      .catch((e) => {
        if (!alive) return;
        const staleData = key ? cacheGetStale(key) : null;
        if (staleData) {
          const hm = staleData.hasMore ?? false;
          setItems(staleData.items ?? []);
          setHasMore(hm);
          hasMoreRef.current = hm;
          setStale(true);
        } else {
          setError(e?.response?.data?.detail || e.message || "Failed to load");
        }
      })
      .finally(() => {
        if (alive) {
          setLoading(false);
          isFetchingRef.current = false;
        }
      });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedKey, ttlMs, initialRetry]);

  // ── Load additional pages ──────────────────────────────────────────────────
  useEffect(() => {
    if (page === 1) return; // handled by the reset effect above

    let alive = true;
    const key = getCacheKeyRef.current?.(page);
    const cached = key && ttlMs > 0 ? cacheGet(key, ttlMs) : null;

    if (cached) {
      const newItems = cached.items ?? [];
      const hm = cached.hasMore ?? (newItems.length >= PAGE_SIZE);
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...newItems.filter((i) => !seen.has(i.id))];
      });
      setHasMore(hm);
      hasMoreRef.current = hm;
      isFetchingRef.current = false;
      return;
    }

    setLoadingMore(true);
    setError(null);

    fetchFnRef.current(page)
      .then((result) => {
        if (!alive) return;
        const newItems = Array.isArray(result) ? result : (result?.items ?? []);
        if (newItems.length === 0) {
          setHasMore(false);
          hasMoreRef.current = false;
          return;
        }
        const hm = result?.has_more ?? result?.hasMore ?? (newItems.length >= PAGE_SIZE);
        setItems((prev) => {
          const seen = new Set(prev.map((i) => i.id));
          return [...prev, ...newItems.filter((i) => !seen.has(i.id))];
        });
        setHasMore(hm);
        hasMoreRef.current = hm;
        if (key) cacheSet(key, { items: newItems, hasMore: hm });
      })
      .catch((e) => {
        if (!alive) return;
        // Revert page so the user can retry via loadMore / retryMore
        setPage((p) => p - 1);
        setError(e?.response?.data?.detail || e.message || "Failed to load more");
      })
      .finally(() => {
        if (alive) {
          setLoadingMore(false);
          isFetchingRef.current = false;
        }
      });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, ttlMs]);

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Triggered by the IntersectionObserver sentinel — loads the next page. */
  const loadMore = useCallback(() => {
    if (isFetchingRef.current || !hasMoreRef.current) return;
    isFetchingRef.current = true;
    setPage((p) => p + 1);
  }, []);

  /** Re-run the initial load (after an error on page 1). */
  const retryInitial = useCallback(() => {
    if (isFetchingRef.current) return;
    setError(null);
    setInitialRetry((n) => n + 1);
  }, []);

  /** Retry a failed "load more" (after an error on page 2+). */
  const retryMore = useCallback(() => {
    setError(null);
    loadMore();
  }, [loadMore]);

  return {
    items,
    loading,
    loadingMore,
    hasMore,
    error,
    stale,
    loadMore,
    retryInitial,
    retryMore,
    page,
  };
}
